if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    static preloadObserverShared = null;
    static preloadObserverTargets = null;

    static getPreloadObserver() {
      if (MediaGallery.preloadObserverShared) return MediaGallery.preloadObserverShared;

      MediaGallery.preloadObserverTargets = MediaGallery.preloadObserverTargets || new Map();
      MediaGallery.preloadObserverShared = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const instance = MediaGallery.preloadObserverTargets.get(entry.target);
          if (!instance) return;
          instance.preloadImage(entry.target);
          MediaGallery.unobservePreloadTarget(entry.target);
        });
      }, {
        root: null,
        rootMargin: '200px'
      });

      return MediaGallery.preloadObserverShared;
    }

    static observePreloadTarget(instance, target) {
      if (!instance || !target) return;
      MediaGallery.preloadObserverTargets = MediaGallery.preloadObserverTargets || new Map();
      MediaGallery.preloadObserverTargets.set(target, instance);
      MediaGallery.getPreloadObserver().observe(target);
    }

    static unobservePreloadTarget(target) {
      if (!target || !MediaGallery.preloadObserverShared) return;

      MediaGallery.preloadObserverShared.unobserve(target);
      MediaGallery.preloadObserverTargets?.delete(target);

      if (MediaGallery.preloadObserverTargets?.size === 0) {
        MediaGallery.preloadObserverShared.disconnect();
        MediaGallery.preloadObserverShared = null;
        MediaGallery.preloadObserverTargets = null;
      }
    }

    static unobserveInstanceTargets(instance) {
      if (!instance || !MediaGallery.preloadObserverTargets?.size) return;

      Array.from(MediaGallery.preloadObserverTargets.entries()).forEach(([target, owner]) => {
        if (owner === instance) MediaGallery.unobservePreloadTarget(target);
      });
    }

    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        thumbnailsScroll: this.querySelector('[id^="GalleryScrollHorizontal"]')
      };
      this.mql = window.matchMedia('(min-width: 577)');

      if (this.closest('quickview-modal')) {
        this.quickView = true;
      }

      this.hasPreloaded = false;
      this.preloadQueue = [];
      this.preloadQueuedSet = new WeakSet();
      this.preloadRaf = null;
      this.maxPreloadsPerFlush = window.DeviceUtils?.isMobile ? 1 : 2;
      this.slideChangedRaf = null;
      this._isUpdatingMedia = false;

      if (this.elements.viewer) {
        this.initObserver();
        this.elements.viewer.addEventListener('slideChanged', (event) => {
          if (this._isUpdatingMedia) return;

          if (this.slideChangedRaf) cancelAnimationFrame(this.slideChangedRaf);
          this.slideChangedRaf = requestAnimationFrame(() => {
            this.slideChangedRaf = null;
            this.onSlideChanged(event);
          });
        });
        const firstSlide = this.elements.viewer.querySelector('[data-media-id]');
        if (firstSlide) this.preloadNeighborImages(firstSlide);
      }

      if (!this.elements.thumbnails) return;
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        const btn = mediaToSwitch.querySelector('button');
        btn.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, false));
        btn.addEventListener('focus', (e) => {
          if (e.relatedTarget !== null) this.setActiveMedia(mediaToSwitch.dataset.target, false, false);
        });
        btn.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaToSwitch.dataset.target}"]`);
          const perSlideBtn = activeMedia?.querySelector('.product__media-toggle');
          const zoomBtn = perSlideBtn || this.querySelector('.product__media-toggle');
          if (!zoomBtn) return;
          const modalId = zoomBtn.closest('modal-opener')?.dataset.modal;
          const modal = modalId ? document.querySelector(modalId) : null;
          if (modal) {
            modal._returnFocusEl = btn;
            if (!perSlideBtn && activeMedia) {
              modal._preferredMediaId = activeMedia.getAttribute('data-media-modal-id') || activeMedia.getAttribute('data-media-id');
            }
          }
          zoomBtn.click();
        });
        btn.addEventListener('mouseenter', () => this.preloadThumbnailImage(mediaToSwitch.dataset.target));
      });

      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      this.addEventListener('touchstart', this.handleFirstTouch.bind(this), { once: true });
    }

    initObserver() {
      this.elements.viewer?.querySelectorAll('img[data-src]').forEach((img) => {
        MediaGallery.observePreloadTarget(this, img);
      });
    }

    handleFirstTouch() {
      if (this.hasPreloaded) return;
      const firstSlide = this.elements.viewer?.querySelector('[data-media-id]');
      if (firstSlide) this.preloadNeighborImages(firstSlide);
      this.hasPreloaded = true;
    }

    preloadAllImages() {
      const allImages = this.elements.viewer?.querySelectorAll('img[data-src]');
      if (!allImages || allImages.length === 0) return;
      allImages.forEach(img => this.enqueuePreload(img));
    }

    preloadImage(img) {
      if (!img) return;
      if (!img.dataset.src && !img.dataset.srcset) return;
      img.classList.add('is-loading');
      if (img.dataset.src) {
        img.src = img.dataset.src;
        delete img.dataset.src;
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        delete img.dataset.srcset;
      }
      const removeLoadingAttr = () => {
        img.classList.remove('is-loading');
        img.removeEventListener('loadstart', removeLoadingAttr);
      };

      if (img.complete) {
        img.classList.remove('is-loading');
      } else {
        img.addEventListener('loadstart', removeLoadingAttr);
        requestAnimationFrame(() => img.classList.remove('is-loading'));
      }
    }

    enqueuePreload(img) {
      if (!img || this.preloadQueuedSet.has(img)) return;
      this.preloadQueuedSet.add(img);
      this.preloadQueue.push(img);
      this.flushPreloadQueue();
    }

    flushPreloadQueue() {
      if (this.preloadRaf) return;

      const run = () => {
        this.preloadRaf = null;
        if (document.visibilityState !== 'visible') return;
        if (!this.isConnected) return;

        let processed = 0;
        while (this.preloadQueue.length && processed < this.maxPreloadsPerFlush) {
          const img = this.preloadQueue.shift();
          if (img?.isConnected) this.preloadImage(img);
          processed += 1;
        }

        if (this.preloadQueue.length) this.flushPreloadQueue();
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          this.preloadRaf = requestAnimationFrame(run);
        }, { timeout: 1200 });
        return;
      }
      this.preloadRaf = requestAnimationFrame(run);
    }

    preloadNeighborImages(slide) {
      if (!slide) return;
      [slide.previousElementSibling, slide, slide.nextElementSibling].forEach(s => {
        const img = s?.querySelector('img[data-src]');
        if (img) this.enqueuePreload(img);
      });
    }

    preloadThumbnailImage(targetId) {
      const targetSlide = (this.elements.viewer || this).querySelector(`[data-media-id="${targetId}"]`);
      if (!targetSlide) return;

      const slides = [];
      let current = targetSlide;
      while (current) {
        slides.unshift(current);
        current = current.previousElementSibling;
      }

      slides.forEach(slide => {
        const img = slide.querySelector('img[data-src]');
        if (img) this.enqueuePreload(img);
      });
    }

    onSlideChanged(event) {
      if (!event?.detail?.currentElement) return;

      const current = event.detail.currentElement;

      let thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${current.dataset.mediaId}"]`
      );

      if (!thumbnail && current.dataset.mediaPosition) {
        thumbnail = this.elements.thumbnails?.querySelector(
            `[data-media-position="${current.dataset.mediaPosition}"]`
        );
      }

      this.preloadNeighborImages(current);

      if (!this.elements.thumbnails) return;

      this.setActiveThumbnail(thumbnail, false);
    }

    setActiveMedia(mediaId, scrollToActive = true, fadeSlide = false) {
      if (this.quickView) scrollToActive = false;
      const mediaContainer = this.elements.viewer || this;
      const activeMedia =
          mediaContainer.querySelector(`[data-media-id="${mediaId}"]`) ||
          mediaContainer.querySelector('[data-media-id]');
      const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);

      if (!activeMedia) return;
      this.preventStickyHeader();

      const isMobileSmall = DeviceUtils.isMobileSmall;

      const isQuickViewColumnLayout =
          this.quickView
            ? (this.dataset.desktopLayout === 'one-column' || this.dataset.desktopLayout === 'two-columns')
            : (!isMobileSmall && this.dataset.desktopLayout === 'one-column');

      const activateThumbnail = () => {
        if (activeThumbnail) {
          const targetSlideIndex = activeThumbnail.dataset.mediaPosition - 1;
          this.elements.viewer?.goToSlide?.(targetSlideIndex, true, fadeSlide);
          this.setActiveThumbnail(activeThumbnail, false);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        } else {
          const slideIndex = +activeMedia.getAttribute('data-media-position') - 1;
          if (slideIndex >= 0) this.elements.viewer?.goToSlide?.(slideIndex, true, fadeSlide);
        }

        if (isQuickViewColumnLayout) {
          requestAnimationFrame(() => this.scrollQuickViewMediaIntoView(activeMedia));
        }

        this.releaseStickyHeader();
      };

      if (this.elements.thumbnails) {
        const rect = activeMedia.getBoundingClientRect();
        if (scrollToActive && rect.top < -0.5) {
          window.scrollTo({ top: rect.top + window.scrollY - 30, behavior: 'smooth' });
          onScrollComplete(activateThumbnail);
        } else {
          activateThumbnail();
        }
      } else {
        activateThumbnail();
      }
    }

    scrollQuickViewMediaIntoView(activeMedia) {
      if (!activeMedia) return;

      const modalContainer =
          this.closest('.info-popup-modal__inside') ||
          this.closest('.has-custom-scrollbar');

      const isWindowScroll = !modalContainer;
      const scrollContainer = modalContainer;

      const isTwoColumns = this.dataset.desktopLayout === 'two-columns';

      const calcTargetScrollTop = () => {
        const mediaRect = activeMedia.getBoundingClientRect();
        const currentScrollTop = isWindowScroll ? window.scrollY : scrollContainer.scrollTop;
        const containerTop = isWindowScroll ? 0 : scrollContainer.getBoundingClientRect().top;
        const offset = isWindowScroll ? 10 : 0;
        return Math.max(0, currentScrollTop + mediaRect.top - containerTop - offset);
      };

      const measureVisibility = () => {
        const mediaRect = activeMedia.getBoundingClientRect();
        const offset = 8;
        const containerTop = isWindowScroll ? 0 : scrollContainer.getBoundingClientRect().top;
        const containerBottom = isWindowScroll ? window.innerHeight : scrollContainer.getBoundingClientRect().bottom;
        const isAboveVisibleArea = mediaRect.top < containerTop + offset;
        const isBelowVisibleArea = mediaRect.bottom > containerBottom - offset;
        return { isAboveVisibleArea, isBelowVisibleArea };
      };

      if (!isTwoColumns) {
        const { isAboveVisibleArea, isBelowVisibleArea } = measureVisibility();
        if (!isAboveVisibleArea && !isBelowVisibleArea) return;
      }

      const targetScrollTop = calcTargetScrollTop();

      if (isWindowScroll) {
        window.scrollTo({ top: targetScrollTop, behavior: 'auto' });
      } else {
        const prevScrollBehavior = scrollContainer.style.scrollBehavior;
        scrollContainer.style.scrollBehavior = 'auto';
        scrollContainer.scrollTop = targetScrollTop;
        requestAnimationFrame(() => {
          scrollContainer.style.scrollBehavior = prevScrollBehavior;
        });
      }
    }

    setActiveThumbnail(thumbnail, scrollToActive = true) {
      const productInfo = this.closest('product-info');
      if (this.quickView || !productInfo?.hasAttribute('data-scroll-active-media')) {
        scrollToActive = false;
      }

      if (!this.elements.thumbnails || !thumbnail) return;
      this.elements.thumbnails.querySelectorAll('button').forEach(el => el.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (scrollToActive) {
        thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      } else {
        const scrollbar = this.elements.thumbnails.querySelector('custom-scrollbar');
        if (scrollbar && typeof scrollbar.scrollToItem === 'function') {
          scrollbar.scrollToItem(thumbnail, false, false, false);
        }
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
        setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    releaseStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('allowHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    disconnectedCallback() {
      MediaGallery.unobserveInstanceTargets(this);

      if (this.preloadRaf) {
        cancelAnimationFrame(this.preloadRaf);
        this.preloadRaf = null;
      }
      if (this.slideChangedRaf) {
        cancelAnimationFrame(this.slideChangedRaf);
        this.slideChangedRaf = null;
      }
    }
  }

  customElements.define('media-gallery', MediaGallery);
}